'use client'

import Spinner from '@/components/icons/spinner'
import { cn } from '@/lib/utils'
import Image from 'next/image'

/**
 * Universal error component to display multiple types of errors.
 * Example Usage:
 ```
  <Loader size="lg" />
 ```
 */
export default function Loader({
  /**
   * The size of the loader.
   * @type {string}
   */
  size = 'md',
  /**
   * Classes to add to elements
   * @type {string}
   */
  className = '',
  /**
   * Whether or not to use the Grid Finder logo for elements that have an `AnimatedElement`
   * @type {boolean | 'animated'}
   */
  logo = false,
  /**
   * String based content that will be put on the loader.
   * @type {string}
   */
  content = '',
  /**
   * Fill colour for the spinner
   * @type {string}
   */
  spinnerFillColor = 'primary',
  /**
   * Background colour for the spinner
   */
  spinnerBgColor = 'neutral-blue-200',
  /**
   * Child components and HTML
   * @type {HTML | JSX}
   */
  children,
}) {
  function AnimatedElement() {
    return (
      <>
        {logo ? (
          <>
            <Image
              className={`mx-auto ${
                logo === 'animated'
                  ? 'animate__animated animate__bounce animate__slower animate__infinite'
                  : ''
              }`}
              src="/img/logos/314x314.webp"
              alt="Grid Finder - The Ultimate Sim Racing Platform"
              width="150"
              height="150"
              priority={true}
            />
          </>
        ) : (
          <div
            className="mx-auto mb-3 inline-block h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent text-primary"
            style={{ '--value': 70 }}
          >
            &nbsp;
          </div>
        )}
      </>
    )
  }

  const elements = {
    sm: (
      <div className={`h-5 w-5 ${className}`}>
        <Spinner fillColor={spinnerFillColor} bgColor={spinnerBgColor} />
      </div>
    ),
    md: (
      <div className={`grid h-64 content-center justify-center ${className}`}>
        <div className="text-inherit">
          {content && <div className="pt-3 text-inherit">{content}</div>}
          <progress className="h-2 w-56 w-full overflow-hidden rounded-full bg-neutral-blue-700 bg-[linear-gradient(90deg,#ff6100_70%,rgba(255,97,0,0.2)_70%)]"></progress>
        </div>
      </div>
    ),
    lg: (
      <div className={`bg-transparent ${className}`}>
        <div className="mx-auto max-w-2xl py-10 text-center">
          <div className="max-w-md font-inter text-xl">
            <AnimatedElement />
            {content && <div className="loader-content">{content}</div>}
            {children && <div className="loader-children">{children}</div>}
          </div>
        </div>
      </div>
    ),
    inline: (
      <progress
        className={`mx-2 h-2 w-10 w-full overflow-hidden rounded-full bg-neutral-blue-700 bg-[linear-gradient(90deg,#ff6100_70%,rgba(255,97,0,0.2)_70%)] ${className}`}
      />
    ),
    overlay: (
      <div
        className={cn(
          'z-999 pointer-events-none fixed bottom-0 left-0 right-0 top-0 flex flex-col flex-wrap content-center justify-center bg-black/80 text-center',
          className,
        )}
      >
        <AnimatedElement />
        <div className="max-w-5xl text-xl text-white">{content}</div>
      </div>
    ),
  }

  return elements[size]
}
